<template>
  <RegisteredField
    v-slot="{ errors }"
    :value="internalValue"
    :rules="textEditorRules"
  >
    <tiptap-vuetify
      ref="editor"
      :key="key"
      v-model="internalValue"
      :extensions="selectedExtensions"
      :placeholder="placeholder"
      :disabled="readonly"
      :class="[
        'work-text-editor',
        reduceHeight ? 'reduce-height' : '',
        hideToolbar ? 'hide-toolbar' : '',
        errors?.length ? 'editor-is-invalid' : ''
      ]"
      @input="(event) => handleInput(event)"
      @focus="(event) => $emit('focus', event)"
      @blur="(event) => $emit('blur', event)"
    >
      <template #toolbar-after>
        <div
          v-if="textBlocksEnabled"
          class="text-block-button-cell"
        >
          <w-btn
            outlined
            class="mt-1 mb-1"
            @click="openTextBlocksDialog"
          >
            {{ $t('Common.textblocks') }}
          </w-btn>
          <TextBlockSelectionDialog
            ref="textBlockDialog"
            :mandatory-filters="textBlockMandatoryFilters"
            :filter-preset="textBlockFilterPreset"
          />
        </div>
        <div
          v-if="allowImages"
          class="align-right"
        >
          <w-camera-button @click="invokeCameraAction" />
        </div>
      </template>
    </tiptap-vuetify>
    <div
      v-if="errors?.length"
      class="is-invalid"
    >
      {{ errors[0] }}
    </div>
  </RegisteredField>
</template>

<script>

// components
import {
  TiptapVuetify,
  Heading,
  Bold,
  Italic,
  Strike,
  Underline,
  Code,
  Paragraph,
  BulletList,
  OrderedList,
  ListItem,
  Link,
  Blockquote,
  HardBreak,
  HorizontalRule,
  History,
  Image
} from 'tiptap-vuetify';
import WorkTextEditorImageUploadArea from './WorkTextEditorImageUploadArea.vue';
import RegisteredField from '@/validation/RegisteredField';
import SNACKBAR_TYPES from '@/utils/enum/SNACKBAR_TYPES';
import { mapActions } from 'vuex';
// dialogs
import TextBlockSelectionDialog from '@/components/global_components/pickers/textblock_picker/TextBlockSelectionDialog';
// mixins
import isRequired from '@/mixins/component/is-required-mixin';
import mobileCameraMixin from '@/mixins/mobile_wrapper/mobile-camera-mixin';
import dataMixin from '@/mixins/data-mixin';

import i18n from "@/language/i18n";

export default {
  name: 'WorkTextEditor',
  components: {
    TextBlockSelectionDialog,
    TiptapVuetify,
    RegisteredField
  },
  mixins: [
    isRequired,
    dataMixin('mobileFiles', 'transientData.mobileFiles'),
    mobileCameraMixin(self => self.handleAddMobileFile, undefined, undefined, true),
  ],
  props: {
    value: {
      type: String,
      default: ''
    },
    label: { type: String, default: undefined },
    allowImages: { type: Boolean, default: false },
    reduceHeight: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
    textBlockMandatoryFilters: undefined,
    textBlockFilterPreset: undefined,
    textBlocksEnabled: { type: Boolean, default: true },
    withoutStore: { type: Boolean, default: false },
    hideToolbar: { type: Boolean, default: false },
    textEditorRules: { type: [Array, String] },
  },
  data() {
    return {
      key: 0,
      extensions: [
        History,
        Blockquote,
        Link,
        Underline,
        Strike,
        Italic,
        ListItem,
        BulletList,
        OrderedList,
        [
          Heading,
          {
            options: {
              levels: [1, 2, 3]
            }
          }
        ],
        Bold,
        Code,
        HorizontalRule,
        Paragraph,
        HardBreak
      ]
    };
  },
  computed: {
    internalValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      }
    },
    selectedExtensions() {
      if (this.allowImages) {
        return [
          ...this.extensions,
          [
            Image,
            {
              options: {
                imageSourcesOverride: true,
                imageSources: [
                  { component: WorkTextEditorImageUploadArea, name: i18n.t('Components.sentences.addImages') }
                ]
              }
            }
          ]
        ];
      }
      return this.extensions;
    },
    placeholder() {
      if (this.label !== undefined && this.required !== undefined && this.required === true)
        return `${this.label}*`;
      return this.label;
    }
  },
  methods: {
    ...mapActions('globalSnackbarStore', ['showSnackbarWithMessage']),
    handleAddMobileFile(base64EncodedFile) {
      let imageTag = "<img src=\"data:image;base64," + base64EncodedFile + "\">";
      this.internalValue = this.internalValue + "<p>" + imageTag + "</p>";

      this.showSnackbarWithMessage({
        type: SNACKBAR_TYPES.SUCCESS,
        message: i18n.t('MobileDeviceBridge.sentences.addedPicture'),
      });
    },
    async openTextBlocksDialog() {
      let newValue = await this.$refs.textBlockDialog.open(this.value);
      this.$emit('input', newValue);
      this.$emit('textBlockResolve');
    },
    handleInput(event) {
      this.$emit('input', event);
    }
  },
  watch: {
    required() {
      // If we have a from element with required = true, we want a placeholder with '*'
      // But the placeholder of tiptap is not reactive and this.required ist still false when tiptap is first loaded
      // We end up with a wrong placeholder
      // Reloading tiptap when this.required changes overcomes this issue
      this.key++;
    },
  }
};
</script>

<style lang="scss" scoped>
.work-text-editor {
  ::v-deep .tiptap-vuetify-editor__content {
    min-height: 14rem;
    max-height: 24rem;
    border: solid;
    border-radius: $border-radius-root;
    border-width: 0.125rem;
    border-color: $input-border-color;
    margin-top: 10px;
    background-color: $input-bg-color;
    &--disabled {
      color: $input-label-disabled-color;
    }
  }

  ::v-deep .tiptap-vuetify-editor__content--disabled:after {
    position: relative;
  }

  ::v-deep .ProseMirror {
    min-height: 13rem !important;
  }

  ::v-deep .v-card {
    box-shadow: none !important;
    display: grid;
    grid-template-columns: 5fr 1fr;
    background-color: $w-unit-bg-color;

    .tiptap-vuetify-editor__toolbar header{
      background-color: $w-unit-bg-color !important;
      border-color: $w-unit-bg-color !important;
      .v-btn {
        background-color: $w-unit-bg-color;
      } 
    }

    div:last-of-type {
      grid-column: 1 / 3;
    }
  }
}

.work-text-editor.reduce-height {
  ::v-deep .tiptap-vuetify-editor__content {
    min-height: 4rem;
    max-height: 8.5rem !important;
  }
}

.text-block-button-cell {
  display: flex;
  background-color: $w-unit-bg-color;
  flex-direction: row;
  justify-content: flex-end;
}

.work-text-editor.hide-toolbar {
  ::v-deep .v-card {
    margin-top: -0.5rem;
    .tiptap-vuetify-editor__toolbar {
      display: none !important;
    }
  }
}

.is-invalid {
    letter-spacing: normal;
    font-size: 12px;
    text-align: left;
    color: rgb(238, 115, 101);
    margin: 8px 0;
    padding-left: 12px;
}

.work-text-editor.editor-is-invalid {
  ::v-deep .tiptap-vuetify-editor__content {
    border: 1px solid rgb(238, 115, 101) !important;
    transition: none !important;
  }
}

.align-right {
  display: flex;
  justify-content: flex-end;
}

::v-deep .tiptap-vuetify-editor__content {
  transition: none !important;
}
</style>
